<template>
  <q-page class="homepage">
    <!-- Hero Section -->
    <div class="hero">
      <img :src="companyLogo" alt="Company Logo" class="hero-logo" />
      <h1 class="title">Modernizing Enterprises & SMBs with Smart Technology</h1>
      <p class="subtitle">We bring efficiency and expertise to help businesses scale through AI, Cloud, Integrations, and Automation.</p>
      <div>Our main focus is to have a simple process that drives tangible and measurable results</div>
      <div class="button-container">
        <!-- New Contact Us Button -->
        <button @click="navigateToContact" class="contact-button">Contact Us</button>
      </div>
    </div>

    <!-- What We Do Section -->
    <section class="what-we-do">
      <h2>How We Help Businesses Evolve</h2>
      <div class="services-grid">
        <div class="service-card" v-for="(service, index) in services" :key="index">
          <component :is="service.icon" class="service-icon" />
          <h3>{{ service.title }}</h3>
          <p>{{ service.description }}</p>
        </div>
      </div>
    </section>

    <!-- Data Pipeline Simulation -->
    <section class="pipeline-comparison">
      <h2>Efficiency in Action</h2>
      <p class="section-description">We optimize operations by accelerating data processing and automation. See the difference:</p>
      <div class="pipeline-container">
        <div class="pipeline slow">
          <h3>Legacy Systems</h3>
          <div class="data-stream">
            <div class="data-chunk slow-chunk" v-for="i in 10" :key="i"></div>
          </div>
        </div>
        <div class="pipeline fast">
          <h3>Optimized Systems</h3>
          <div class="data-stream">
            <div class="data-chunk fast-chunk" v-for="i in 10" :key="i"></div>
          </div>
        </div>
      </div>
    </section>
  </q-page>
</template>

<script setup>
import { useRouter } from 'vue-router';
import companyLogo from '@/assets/HuntsEndHoldings.webp';
import AIIcon from '@/assets/icons/AIIcon.vue';
import CloudIcon from '@/assets/icons/ETLIcon';
import APIIcon from '@/assets/icons/APIIcon.vue';
import AutomationIcon from '@/assets/icons/MicroservicesIcon';

const router = useRouter();

function navigateToContact() {
  router.push('/contact');
}

const services = [
  { title: "AI & ML", description: "Enhance business intelligence with custom AI solutions.", icon: AIIcon },
  { title: "Cloud Solutions", description: "Optimize your infrastructure with scalable cloud technologies.", icon: CloudIcon },
  { title: "API-Led Integrations", description: "Seamlessly connect your systems with API-driven architectures.", icon: APIIcon },
  { title: "Enterprise Automation", description: "Reduce inefficiencies and streamline operations with automation.", icon: AutomationIcon }
];
</script>

<style scoped>
.homepage {
  text-align: center;
  color: white;
  background: #111;
  padding: 50px 20px;
  position: relative;
}
.hero {
  padding: 100px 20px;
  background: linear-gradient(135deg, #ff9800, #ff5722);
  border-radius: 10px;
  margin-top: 0;
  width: 100%;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);
}
.hero-logo {
  max-width: 150px;
  margin-bottom: 20px;
}
.title {
  font-size: 2rem;
  margin-bottom: 10px;
}
.subtitle {
  font-size: 1rem;
  margin-bottom: 20px;
}
.button-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  gap: 10px;
}
.cta-button {
  background: linear-gradient(135deg, #ff9800, #ff5722);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.3s ease;
}

.cta-button:hover {
  background: linear-gradient(135deg, #ff5722, #ff9800);
}

.what-we-do {
  margin-top: 50px;
  text-align: center;
}
.services-grid {
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
}
.service-card {
  background: linear-gradient(135deg, #ff9800, #ff5722);
  padding: 15px;
  border-radius: 10px;
  width: 250px;
  text-align: left;
  font-size: 0.9rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.service-image {
  width: 80px;
  height: auto;
  margin-bottom: 10px;
}
.pipeline-comparison {
  margin-top: 50px;
}
.pipeline-container {
  display: flex;
  justify-content: center;
  gap: 40px;
  flex-wrap: wrap;
}
.pipeline {
  width: 45%;
  max-width: 500px;
  padding: 20px;
  background: linear-gradient(135deg, #ff9800, #ff5722);
  border-radius: 10px;
  text-align: center;
  position: relative;
}
.data-stream {
  display: flex;
  justify-content: space-around;
  height: 15px;
  margin-top: 20px;
  position: relative;
}
.data-chunk {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ffffff; /* Change to white for contrast */
  position: absolute;
  animation: moveData linear infinite;
}
.slow-chunk {
  animation-duration: 4s;
}
.fast-chunk {
  animation-duration: 1s;
}
@keyframes moveData {
  0% { left: 0; }
  100% { left: 100%; }
}

@media (max-width: 768px) {
  .services-grid {
    flex-direction: column;
    align-items: center;
  }
  .service-card {
    width: 90%;
    font-size: 0.85rem;
    padding: 12px;
  }
  .pipeline-container {
    flex-direction: column;
    align-items: center;
  }
  .pipeline {
    width: 90%;
  }
  .hero {
    padding: 120px 20px;
    width: 90%;
  }
}

.contact-button {
  background: #333; /* Darker color for visibility */
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.3s ease;
}

.contact-button:hover {
  background: #555; /* Slightly lighter on hover */
}
</style>
